// Breakable containers (B6, monorail-n9v). Sessile per-biome furniture —
// cysts in the Shallow Pool, filing cabinets in the deep office water,
// spore pods on the mushroom floor — cracked open by the attack verb
// (melee lash in attack.ts, ranged shots in projectiles.ts both route
// through hitContainer). Every break pays a small DNA orb (always worth
// the swipe); a low, depth-scaled item roll keeps gear drops evented and
// BELOW predator odds, so furniture never out-drops fights.
//
// Placement comes from dungeon/furnish.ts (deterministic per seed).
// Broken containers stay broken for the session — respawn clears the
// world, so the session set lives here at module scope; a fresh boot
// restocks the rooms.

import type { ContainerKind, Entity } from '../components'
import type { GameWorld } from '../world'
import type { EventBus } from '../events'
import type { DungeonLayout } from '../dungeon/types'
import type { CreatureRecipe } from '../creature-recipe'
import { furnishContainers } from '../dungeon/furnish'
import { currentBiomeAt } from '../biomes'
import type { BiomeId } from '../biomes'
import { morphologyFrom } from '../creature-recipe'
import { depthFactor } from '../depth'
import { rollItem } from '../items'
import { spawnItemOrb } from './item-orb'
import { spawnDnaOrb } from './dna-orb'
import { spawnInkSplatter, spawnPigmentPuff, emitRipple } from './particles'
import { playSfx, panFor } from '../../engine/audio'
import { T } from '../tunables'

const KIND_BY_BIOME: Record<BiomeId, ContainerKind> = {
  shallow: 'cyst',
  deep: 'cabinet',
  mushroom: 'sporePod',
}

// Looks: rigid-walled bodies so they read as objects, not creatures (no
// eyes, no wander — stillness is the "furniture" tell).
const KIND_RECIPE: Record<ContainerKind, CreatureRecipe> = {
  cyst:     { body: 'blob', ornaments: ['cellWall', 'vacuoles'] },
  cabinet:  { body: 'plantCell', ornaments: ['cellWall'] },  // rounded box = drawer
  sporePod: { body: 'mushroom', ornaments: ['gills'] },
}

const KIND_TINT: Record<ContainerKind, [number, number, number]> = {
  cyst:     [0.88, 0.80, 0.62], // waxy membrane sand
  cabinet:  [0.74, 0.70, 0.60], // government manila
  sporePod: [0.70, 0.60, 0.78], // dusty lavender
}

// Session-broken fixture ids (see module comment).
const brokenThisSession = new Set<number>()

/** Test hook. */
export function resetBrokenContainers(): void {
  brokenThisSession.clear()
}

/** Seed the world's furniture — called from seedDemo on boot AND respawn
 *  (respawn clears the world; the broken set carries the depletion). */
export function spawnContainers(world: GameWorld, layout: DungeonLayout, seed: number): void {
  const c = T.containers
  for (const spot of furnishContainers(layout, seed)) {
    if (brokenThisSession.has(spot.id)) continue
    const biome = currentBiomeAt(spot.x, spot.y)
    const kind = KIND_BY_BIOME[biome]
    world.add({
      position: { x: spot.x, y: spot.y },
      // The faintest breathing — alive-adjacent, but rooted.
      wobble: { phase: Math.random() * Math.PI * 2, freq: 0.5, amp: 0.02 },
      renderable: { shape: 'microbe', radius: c.radius, tint: KIND_TINT[kind], variant: biome },
      morphology: morphologyFrom(KIND_RECIPE[kind], Math.random()),
      container: { kind, fixtureId: spot.id },
      health: { current: c.health, max: c.health },
    })
  }
}

/** Shared hit path for melee + shots. Applies damage, stages the feedback,
 *  breaks + pays out at zero. No lifesteal from furniture — healing off a
 *  filing cabinet would make containers a free sustain fountain. */
export function hitContainer(
  world: GameWorld,
  ent: Entity,
  damage: number,
  player: Entity,
  events: EventBus,
  rng: () => number = Math.random,
): void {
  if (!ent.health || !ent.container) return
  ent.health.current -= damage
  ent.hitFlash = T.damage.hitFlashDuration
  events.emit({ type: 'damage.dealt', target: ent, amount: damage })
  playSfx('dash_impact', { volume: 0.25, pan: panFor(ent.position.x, player.position.x) })
  if (ent.health.current > 0) {
    // Cracked, not broken: a puff of its own pigment sells the dent.
    spawnPigmentPuff(world, ent.position.x, ent.position.y, ent.renderable!.tint, 0.04)
    return
  }
  breakContainer(world, ent, player, rng)
}

function breakContainer(world: GameWorld, ent: Entity, player: Entity, rng: () => number): void {
  const c = T.containers
  const { x, y } = ent.position
  const tint = ent.renderable!.tint
  brokenThisSession.add(ent.container!.fixtureId)

  // The pop: shell shards (ink) + contents (pigment) + a wet ripple.
  spawnInkSplatter(world, x, y)
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2
    spawnPigmentPuff(world, x + Math.cos(a) * 0.06, y + Math.sin(a) * 0.06, tint, 0.045)
  }
  emitRipple(x, y, { strength: 0.3, lifetime: 0.5 })
  playSfx('eat', { volume: 0.4, pan: panFor(x, player.position.x) })

  // Guaranteed DNA floor; the orb drifts free (not an own-orb).
  const amount = c.dnaMin + Math.floor(rng() * (c.dnaMax - c.dnaMin + 1))
  spawnDnaOrb(world, x, y, amount)

  // Low depth-scaled item roll — same curve shape as predators, lower base.
  const chance = c.itemBase + c.itemDepthBonus * depthFactor(ent)
  if (rng() < chance) {
    const slot = rng() < 0.5 ? 'hat' : 'weapon'
    spawnItemOrb(world, x, y, rollItem(slot, { rng }))
  }

  if (world.id(ent) !== undefined) world.remove(ent)
}
